import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import Breadcrumbs from './Breadcrumbs';
import Toast from '../ui/Toast';

/**
 * MainLayout Component
 * Root layout wrapper for the entire admin panel
 * Manages responsive sidebar state and global UI elements
 * Desktop: sidebar always visible, collapsible
 * Mobile: sidebar as overlay drawer
 */

export default function MainLayout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close mobile menu on navigation
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const handleMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'new-notification':
        navigate('/notifications/new');
        break;
      case 'new-agent':
        navigate('/agents/new');
        break;
      default:
        console.log('Quick action:', action);
    }
  };

  // Check if current route is the map page
  const isMapPage = location.pathname === '/map';

  return (
    <div className="h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-cyan-50/20 overflow-hidden flex flex-col">
      {/* Header - fixed at top */}
      <Header
        onMenuToggle={handleMenuToggle}
        onQuickAction={handleQuickAction}
      />

      {/* Sidebar - fixed position */}
      <Sidebar
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        isMobile={isMobile}
        onCollapseChange={setIsSidebarCollapsed}
      />

      {/* Main content area */}
      <div className={`flex-1 flex flex-col pt-16 transition-all duration-300 overflow-hidden ${
        isMobile ? '' : isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'
      }`}>
        {!isMapPage && <Breadcrumbs />}

        <main className={`flex-1 animate-fade-in ${
          isMapPage ? 'overflow-hidden' : 'overflow-y-auto p-4 sm:p-6 lg:p-8'
        }`}>
          <Outlet />
        </main>
      </div>

      {/* Toast notifications */}
      <Toast />
    </div>
  );
}